import type { ExecutionEventType } from '@ctcells/contract'
import { useSyncExternalStore } from 'react'

import { batchOfExecution, executionEventType, stepAction } from './progress.ts'

/**
 * 한 작업의 **스텝 줄** 보관소 — 상시 작업 줄 이벤트 칸이 원장 행과 섞어 그린다(eric 2026-09-28
 * 「상태가 아니라 스텝별 내용을 보여줄 수 없어?」).
 *
 * 🔴 **서버는 스텝을 저장하지 않는다.** 스텝(「move_to 완료」 · n/N)은 구독 `batch_progress` 로만
 *    오고 `/history` 에 없다(실측 2026-09-28). 그래서 **이 화면을 연 뒤 받은 것만** 있다 —
 *    새로 고치면 사라진다(화면의 안내 한 줄은 2026-09-29 걷었다).
 *    ⚠ 브라우저 저장을 하지 않는다 — 한 작업이 스텝 수천 개다(실측 4,211).
 * 🔴 **스텝이 «바뀌는» 프레임만** 쌓는다. `progress`(1Hz 심장박동·시작 알림)·`telemetry` 는 버린다.
 * 🔴 **앱 셸의 구독 배선이 채운다**(`useStreamBridge`). 이벤트 칸이 접혀 있어도 계속 쌓인다 —
 *    칸이 펼칠 때만 선다고 여기까지 같이 죽으면 접었다 펴는 순간 줄이 날아간다.
 * ⚠ stale 에도 **지우지 않는다** — 이미 받은 스텝은 과거 기록이다(「지금 상태」가 아니다).
 *   끊긴 동안의 스텝은 다시 오지 않는다 — 그 사실은 칸의 끊김 줄이 말한다.
 * 🔴 **같은 사건이 두 번 온다**(실측 2026-11-02 · 고유 사건 58건 중 28건 · `version` · `ts` 까지 같은 프레임이 수 ms 차이로). module-core 가 라이브 발행과
 *    장부 비우기로 같은 봉투를 두 번 낼 수 있고(`publisher.py` 「상위가 `(execution_id, step, type)` 으로 한 건으로 접는다」), 코어 중계는 접지 않는다.
 *    그래서 여기서 접는다(`isRepeat`). ⚠ 열쇠에 `version` 을 더 넣는다 — 같은 칸의 일시정지 → 재개 → 일시정지는 다른 사건이다.
 * 🔴 **`started` 프레임에는 이름이 없다**(`sequence` 키 없음 · 원자가 아직 없다). 같은 실행의 다음 프레임(`progress` step 1 — 쌓지는 않는다)이 첫 원자 이름을
 *    싣고 오므로 그 값을 시작 줄에 잇는다(`nameStartedRow`). 서버가 준 값을 잇는 것이고 지어내지 않는다 — step 1 이 아닌 프레임이면 잇지 않는다.
 */
export interface StepLogRow {
  /** 클라이언트 수신 순번. 같은 시각·스텝의 별개 사건도 구분한다. 서버 상태 값이 아니다. */
  receivedOrder?: number
  /** 봉투 시각(ISO) */
  ts: string
  eventType: ExecutionEventType
  /** 동작 원값(`move_to` …). 🔴 한글 표기는 표시 계층의 몫이다 */
  action?: string
  message?: string
  step?: number
  total?: number
  moduleId?: string
  /** 실행 식별자 — 시작 줄에 이름을 잇고 재발행을 접는 열쇠 */
  executionId?: string
  /** 원 출처 번호(`payload.version`). 같은 실행 · 같은 칸의 다른 사건을 가른다 */
  version?: number
}

/** 쌓는 갈래 — 스텝이 바뀌는 것만(`progress` · `telemetry` 는 뺀다) */
const STEP_EVENT_TYPES: ReadonlySet<ExecutionEventType> = new Set<ExecutionEventType>([
  'started',
  'step_complete',
  'complete',
  'error',
  'cancelled',
  'aborted',
  'paused',
  'resumed',
])

/** 작업당 상한. 넘치면 **오래된 스텝 줄부터** 버린다 */
export const STEP_LOG_LIMIT = 500
/** 추적할 작업 수 상한. 넘치면 가장 오래 전에 받은 작업을 버린다 */
const MAX_BATCHES = 8

const EMPTY: readonly StepLogRow[] = []
let log: ReadonlyMap<string, readonly StepLogRow[]> = new Map()
const listeners = new Set<() => void>()
let receivedOrder = 0

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asText(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

function asCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * 구독 프레임 한 건 → 스텝 줄 한 건. 쌓을 것이 아니면 `null`.
 * 🔴 작업 귀속은 진행 보관소와 **같은 규칙**이다 — 봉투 `batch_id`, 없으면 `module_status` 미러의
 *    `execution_id` 대조(`progress.ts` `batchOfExecution`). 여기서 모듈 ↔ 작업을 추론하지 않는다.
 */
export function stepLogRowOf(raw: unknown): { batchId: string; row: StepLogRow } | null {
  const hit = stepRowOf(raw)
  if (hit === null || hit.batchId === undefined) return null
  return { batchId: hit.batchId, row: hit.row }
}

/**
 * 프레임 → 스텝 줄(작업 귀속은 있으면). 🔴 6축 프레임은 `batch_id` 가 없고 미러의 `current_execution.batch_id` 도 `null` 이라(코어가 일부러 뺐다)
 * 작업에 못 잇는다 — 그래도 **모듈별** 줄에는 쌓는다(장비 판의 「처리 중」 그림 아래 한 줄 · eric 2026-10-08 「6축 로봇에서 아래에 스텝의 내용 보이게 해줘」)
 */
function stepRowOf(raw: unknown): { batchId: string | undefined; row: StepLogRow } | null {
  const frame = asRecord(raw)
  if (frame === null || frame['type'] !== 'batch_progress') return null
  const payload = asRecord(frame['payload']) ?? {}
  const eventType = executionEventType(payload['type'])
  if (eventType === undefined || !STEP_EVENT_TYPES.has(eventType)) return null
  const batchId = asText(frame['batch_id']) ?? batchOfExecution(asText(payload['execution_id']))
  const ts = asText(frame['ts']) ?? asText(payload['ts'])
  if (ts === undefined) return null

  const row: StepLogRow = { ts, eventType }
  const action = stepAction(payload)
  if (action !== undefined) row.action = action
  const message = asText(payload['message'])
  if (message !== undefined) row.message = message
  const step = asCount(payload['step'])
  if (step !== undefined) row.step = step
  const total = asCount(payload['total'])
  if (total !== undefined) row.total = total
  const moduleId = asText(frame['module_id'])
  if (moduleId !== undefined) row.moduleId = moduleId
  const executionId = asText(payload['execution_id'])
  if (executionId !== undefined) row.executionId = executionId
  const version = asCount(payload['version'])
  if (version !== undefined) row.version = version
  return { batchId, row }
}

/** 이미 받은 사건인가 — 재발행. 🔴 열쇠(실행 · 번호)가 없는 프레임은 접지 않는다(모르면 지우지 않는다) */
function isRepeat(row: StepLogRow): boolean {
  if (row.moduleId === undefined || row.executionId === undefined || row.version === undefined)
    return false
  return (moduleLog.get(row.moduleId) ?? EMPTY).some(
    (seen) =>
      seen.executionId === row.executionId &&
      seen.version === row.version &&
      seen.eventType === row.eventType &&
      seen.step === row.step,
  )
}

/** 이름을 기다리는 시작 줄 — 실행 → 그 줄이 쌓인 곳. 이름이 안 오는 실행(곧바로 취소 등)이 남지 않게 상한을 둔다 */
const MAX_UNNAMED_STARTS = 16
const unnamedStarts = new Map<string, { moduleId: string; batchId: string | undefined }>()

function withStartName(
  rows: readonly StepLogRow[],
  executionId: string,
  action: string,
): readonly StepLogRow[] {
  return rows.map((row) =>
    row.executionId === executionId && row.eventType === 'started' ? { ...row, action } : row,
  )
}

/** 같은 실행의 첫 원자 이름을 시작 줄에 잇는다. 바꿨으면 `true` */
function nameStartedRow(raw: unknown): boolean {
  const frame = asRecord(raw)
  if (frame === null || frame['type'] !== 'batch_progress') return false
  const payload = asRecord(frame['payload']) ?? {}
  const executionId = asText(payload['execution_id'])
  const pending = executionId === undefined ? undefined : unnamedStarts.get(executionId)
  if (executionId === undefined || pending === undefined) return false
  const action = stepAction(payload)
  if (action === undefined) return false
  unnamedStarts.delete(executionId)
  // 첫 원자를 놓쳤으면(끊김) 뒤 원자 이름을 시작 줄에 적지 않는다
  if (asCount(payload['step']) !== 1) return false
  const moduleRows = moduleLog.get(pending.moduleId)
  if (moduleRows !== undefined) {
    moduleLog = new Map(moduleLog).set(
      pending.moduleId,
      withStartName(moduleRows, executionId, action),
    )
  }
  const batchRows = pending.batchId === undefined ? undefined : log.get(pending.batchId)
  if (pending.batchId !== undefined && batchRows !== undefined) {
    log = new Map(log).set(pending.batchId, withStartName(batchRows, executionId, action))
  }
  return true
}

function noteUnnamedStart(row: StepLogRow, batchId: string | undefined): void {
  if (row.eventType !== 'started' || row.action !== undefined) return
  if (row.executionId === undefined || row.moduleId === undefined) return
  unnamedStarts.set(row.executionId, { moduleId: row.moduleId, batchId })
  while (unnamedStarts.size > MAX_UNNAMED_STARTS) {
    const oldest = unnamedStarts.keys().next()
    if (oldest.done === true) break
    unnamedStarts.delete(oldest.value)
  }
}

export function pushStepLog(raw: unknown): void {
  const named = nameStartedRow(raw)
  const hit = stepRowOf(raw)
  if (hit === null || isRepeat(hit.row)) {
    if (named) for (const listener of listeners) listener()
    return
  }
  hit.row.receivedOrder = ++receivedOrder
  pushModuleStepLog(hit.row)
  noteUnnamedStart(hit.row, hit.batchId)
  if (hit.batchId === undefined) {
    // 모듈 직접 실행은 작업 귀속 없이도 모듈 보관소를 갱신한다.
    if (hit.row.moduleId !== undefined) for (const listener of listeners) listener()
    return
  }
  const prev = log.get(hit.batchId) ?? EMPTY
  const rows = [...prev, hit.row]
  const next = new Map(log)
  next.delete(hit.batchId)
  next.set(hit.batchId, rows.length > STEP_LOG_LIMIT ? rows.slice(-STEP_LOG_LIMIT) : rows)
  while (next.size > MAX_BATCHES) {
    const oldest = next.keys().next()
    if (oldest.done === true) break
    next.delete(oldest.value)
  }
  log = next
  for (const listener of listeners) listener()
}

/** 구독 배선이 내려갈 때(로그아웃 등) 버린다. 🔴 stale 에는 부르지 않는다 */
export function resetStepLog(): void {
  unnamedStarts.clear()
  if (log.size === 0 && moduleLog.size === 0) return
  log = new Map()
  moduleLog = new Map()
  for (const listener of listeners) listener()
}

// ── 모듈별 스텝 줄 ─────────────────────────────────────────────────────────────

/** 모듈당 상한 — 「마지막 몇 줄」만 쓴다(장비 판 그림 아래 한 줄 · 염색기 정면도의 끝난 동작) */
export const MODULE_STEP_LOG_LIMIT = 50
let moduleLog: ReadonlyMap<string, readonly StepLogRow[]> = new Map()

function pushModuleStepLog(row: StepLogRow): void {
  if (row.moduleId === undefined) return
  const prev = moduleLog.get(row.moduleId) ?? EMPTY
  const rows = [...prev, row]
  const next = new Map(moduleLog)
  next.set(
    row.moduleId,
    rows.length > MODULE_STEP_LOG_LIMIT ? rows.slice(-MODULE_STEP_LOG_LIMIT) : rows,
  )
  moduleLog = next
}

/** 그 모듈의 스텝 줄 — 받은 차례(오래된 → 최근) · 작업 귀속과 무관(6축 포함) */
export function selectModuleStepLog(moduleId: string | undefined): readonly StepLogRow[] {
  return moduleId === undefined ? EMPTY : (moduleLog.get(moduleId) ?? EMPTY)
}

export function useModuleStepLog(moduleId: string | undefined): readonly StepLogRow[] {
  return useSyncExternalStore(
    subscribe,
    () => selectModuleStepLog(moduleId),
    () => selectModuleStepLog(moduleId),
  )
}

/** 받은 차례(오래된 → 최근) */
export function selectStepLog(batchId: string | undefined): readonly StepLogRow[] {
  return batchId === undefined ? EMPTY : (log.get(batchId) ?? EMPTY)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useBatchStepLog(batchId: string | undefined): readonly StepLogRow[] {
  return useSyncExternalStore(
    subscribe,
    () => selectStepLog(batchId),
    () => selectStepLog(batchId),
  )
}
